import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { RootState } from "../app/store";


const SUPPORTED_COUNTRIES = [
  {
    name: "Nigeria",
    currency: "NGN",
    country_code: "NG",
    dialCode: "+234",
    flag: "https://flagcdn.com/w320/ng.png",
  },
  {
    name: "Ghana",
    currency: "GHS",
    country_code: "GH",
    dialCode: "+233",
    flag: "https://flagcdn.com/w320/gh.png",
  },
  {
    name: "Kenya",
    currency: "KES",
    country_code: "KE",
    dialCode: "+254",
    flag: "https://flagcdn.com/w320/ke.png",
  },
  {
    name: "South Africa",
    currency: "ZAR",
    country_code: "ZA",
    dialCode: "+27",
    flag: "https://flagcdn.com/w320/za.png",
  }
]; 

interface AppState {
  countries: { name: string; currency: string, country_code: string, dialCode: string, flag: string }[];
  currencies: string[];
  userLocation: { country: string; currency: string, country_code: string } | null;
  status: "idle" | "loading" | "succeeded" | "failed";
}

const initialState: AppState = {
  countries: [],
  currencies: [],
  userLocation: null,
  status: "idle",
};

export const initAppData = createAsyncThunk("app/initData", async () => {
  const formattedCountries = SUPPORTED_COUNTRIES; 
  // Fetch Location
  let locationData = { country: "Nigeria", currency: "NGN", country_code: "NG" };
  
  try {
    const locationRes = await fetch("https://ipwho.is/");
    if (locationRes.ok) {
      const data = await locationRes.json();
      
      // Only update location if the user is actually in one of your supported countries
      const isSupported = formattedCountries.some(c => c.country_code === data.country_code);
      
      if (data.success && isSupported) {
        locationData = {
          country: data.country,
          currency: data.currency_code,
          country_code: data.country_code
        };
      }
    }
  } catch (e) {
    console.warn("Location detection failed, using default (Nigeria).");
  }

  // Process Currencies
  const uniqueCurrencies = Array.from(
    new Set(formattedCountries.map((c: any) => c.currency))
  ).filter(Boolean).sort() as string[];

  return {
    countries: formattedCountries,
    currencies: uniqueCurrencies,
    userLocation: locationData,
  };
});

const appSlice = createSlice({
  name: "app",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(initAppData.pending, (state) => {
        state.status = "loading";
      })
      .addCase(initAppData.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.countries = action.payload.countries;
        state.currencies = action.payload.currencies;
        state.userLocation = action.payload.userLocation;
      })
      .addCase(initAppData.rejected, (state) => {
        state.status = "failed";
        // Fallback to prevent app lock-up
        console.log('failed')
        state.userLocation = { country: "Nigeria", currency: "NGN", country_code: "NG" };
      });
  },
});

export const selectGlobalData = (state: RootState) => state.app;

export default appSlice.reducer;